import { NextRequest, NextResponse } from 'next/server';
import { getTransporter, buildEmailHTML } from '@/lib/nodemailer';

interface SendMailBody {
  customer: {
    customerName: string;
    companyName: string;
    email: string;
    phone: string;
    country: string;
  };
  product: {
    productName: string;
    description: string;
  };
  material: {
    material: string;
    finish: string;
    color: string;
  };
  dimensions: {
    height: string;
    width: string;
    depth: string;
    weight: string;
  };
  referenceImage: string | null;
  referenceImageName: string | null;
  selectedImage: string | null;
}

function parseUserAgent(userAgent: string) {
  if (!userAgent) {
    return {
      browser: 'Unknown',
      os: 'Unknown',
      device: 'Unknown',
    };
  }

  let browser = 'Unknown';
  let os = 'Unknown';
  let device = 'Desktop';

  // Browser
  if (userAgent.includes('Edg/')) {
    browser = 'Microsoft Edge';
  } else if (userAgent.includes('Chrome/')) {
    browser = 'Google Chrome';
  } else if (userAgent.includes('Firefox/')) {
    browser = 'Mozilla Firefox';
  } else if (
    userAgent.includes('Safari/') &&
    !userAgent.includes('Chrome/')
  ) {
    browser = 'Safari';
  } else if (
    userAgent.includes('OPR/') ||
    userAgent.includes('Opera')
  ) {
    browser = 'Opera';
  }

  // Operating System
  if (userAgent.includes('Windows')) {
    os = 'Windows';
  } else if (userAgent.includes('Mac OS X')) {
    os = 'macOS';
  } else if (userAgent.includes('Android')) {
    os = 'Android';
    device = 'Mobile';
  } else if (
    userAgent.includes('iPhone') ||
    userAgent.includes('iPad')
  ) {
    os = 'iOS';
    device = 'Mobile';
  } else if (userAgent.includes('Linux')) {
    os = 'Linux';
  }

  // Device
  if (/Tablet|iPad/i.test(userAgent)) {
    device = 'Tablet';
  } else if (/Mobile|Android|iPhone|iPod/i.test(userAgent)) {
    device = 'Mobile';
  }

  return {
    browser,
    os,
    device,
  };
}

export async function POST(request: NextRequest) {
  try {
    const body: SendMailBody = await request.json();

    const {
      customer,
      product,
      material,
      dimensions,
      referenceImage,
      selectedImage,
    } = body;

    // Validate required fields
    if (
      !customer?.customerName ||
      !customer?.email ||
      !product?.productName
    ) {
      return NextResponse.json(
        {
          error:
            'Missing required fields: customer name, email or product name.',
        },
        { status: 400 }
      );
    }

    // Client IP
    const forwarded = request.headers.get('x-forwarded-for');

    const userIP =
      forwarded?.split(',')[0].trim() ||
      request.headers.get('x-real-ip') ||
      'Unknown';

    // User Agent
    const ua = request.headers.get('user-agent') || '';

    const { browser, os, device } = parseUserAgent(ua);

    // Date
    const submissionDate = new Date().toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });

    // Email HTML
    const html = buildEmailHTML({
      customerName: customer.customerName,
      companyName: customer.companyName,
      email: customer.email,
      phone: customer.phone,
      country: customer.country,

      productName: product.productName,
      description: product.description,

      material: material.material,
      finish: material.finish,
      color: material.color,

      height: dimensions.height,
      width: dimensions.width,
      depth: dimensions.depth,
      weight: dimensions.weight,

      referenceImage,
      selectedImage,

      submissionDate,
      userIP,
      browser,
      os,
    });

    const mailTo = process.env.MAIL_TO || 'sales@company.com';

    // Development Mode
    if (!process.env.SMTP_USER || !process.env.SMTP_PASS) {
      console.warn(
        'SMTP credentials missing. Email sending skipped.'
      );

      return NextResponse.json({
        success: true,
        message:
          'Inquiry received successfully. SMTP credentials are not configured.',
      });
    }

    const transporter = getTransporter();

    await transporter.sendMail({
      from: `"AI Product Builder" <${process.env.SMTP_USER}>`,
      to: mailTo,
      replyTo: customer.email,
      subject: `🏭 New AI Product Inquiry — ${product.productName} | ${customer.customerName}`,
      html,
    });

    return NextResponse.json({
      success: true,
      message: 'Email sent successfully.',
    });
  } catch (error) {
    console.error('Mail Error:', error);

    return NextResponse.json(
      {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Something went wrong.',
      },
      {
        status: 500,
      }
    );
  }
}